import { ComponentType } from 'react';
import { IJob } from '~/models';
import { JobStatusEnum } from '~/models/Job';
import { CancelledExpiredPage } from './CancelledExpiredPage';
import { CompletedShiftDetailsPage } from './CompletedShiftDetailsPage/CompletedShiftDetailsPage';
import { ConfirmCandidatesPage } from './ConfirmCandidatesPage/ConfirmCandidatesPage';
import { OpenShiftDetailsPage } from './OpenShiftDetailsPage/OpenShiftDetailsPage';
import { ScheduledShiftDetailsPage } from './ScheduledShiftDetailsPage/ScheduledShiftDetailsPage';

/**
 * The Shift Details states the redesign ships as separate pages. Each is built
 * by its own ticket and added to `SHIFT_DETAILS_PAGES`; the shell and the gate
 * stay agnostic of which ones exist yet.
 */
export type TShiftDetailsPageKey =
  | 'open'
  | 'confirm-candidates'
  | 'scheduled'
  | 'completed'
  | 'cancelled-expired';

/** Props every state page receives from the gate. */
export interface IShiftDetailsPageProps {
  job: IJob;
}

/**
 * Job status → redesign page. Mirrors the routing the legacy screens do today:
 * `pending`/`counter` land on Confirm Candidate, a confirmed shift lands on the
 * scheduled view, a finished one on the completed view, and everything terminal
 * is the cancelled/expired view.
 *
 * Statuses with no redesigned page return `null`, which the gate reads as
 * "fall through to the legacy screen" — so a status nobody designed for can
 * never render a blank page.
 *
 * `pay-out` and `adjusted-required` are deliberately absent, so they resolve to
 * `null` and keep the legacy screen. They are not undesigned states: they drive
 * the End of Day, rating and adjustment flows, which already work and which this
 * redesign does not touch. Routing them to the `scheduled` page — as this map did
 * before the COMPLETED page existed — would offer "Message Pro" and "Cancel" on a
 * shift that has already been worked.
 */
export function shiftDetailsPageForStatus(status?: JobStatusEnum): TShiftDetailsPageKey | null {
  switch (status) {
    case JobStatusEnum.Open:
      return 'open';

    case JobStatusEnum.Pending:
    case JobStatusEnum.Counter:
    case JobStatusEnum.PendingCounter:
      return 'confirm-candidates';

    case JobStatusEnum.Filled:
      return 'scheduled';

    case JobStatusEnum.Complete:
      return 'completed';

    case JobStatusEnum.Cancelled:
    case JobStatusEnum.CancelledHyg:
    case JobStatusEnum.CancelledDnt:
    case JobStatusEnum.Expired:
      return 'cancelled-expired';

    default:
      return null;
  }
}

/**
 * The state pages built so far. A status with no entry here falls through to its
 * legacy screen, flag on or off, so each page ticket activates its own status by
 * registering itself.
 *
 * Populated statically from this module, so the set of live pages is readable in
 * one place. Do not register by import side effect: a page that has to be
 * imported to exist is a page that silently disappears when the import is
 * tree-shaken or reordered.
 */
export const SHIFT_DETAILS_PAGES: Partial<
  Record<TShiftDetailsPageKey, ComponentType<IShiftDetailsPageProps>>
> = {
  open: OpenShiftDetailsPage,
  'confirm-candidates': ConfirmCandidatesPage,
  scheduled: ScheduledShiftDetailsPage,
  completed: CompletedShiftDetailsPage,
  'cancelled-expired': CancelledExpiredPage,
};
